import React from 'react';
import { Input, DatePicker } from 'antd';
import dayjs from 'dayjs';
import { F9051 } from '../../../services/pw9205Api';
import { Mode } from '../hooks/usePW9205Mode';
import LookupField from '../../../components/LookupField';

const { TextArea } = Input;

interface FieldErrorProps {
  error?: string;
}

const FieldError: React.FC<FieldErrorProps> = ({ error }) =>
  error ? (
    <span style={{ color: '#ff4d4f', fontSize: 11, marginLeft: 6, fontWeight: 'normal' }}>
      {error}
    </span>
  ) : null;

interface FormFieldsProps {
  names: Record<string, string>;
  isEditing: boolean;
  mode: Mode;
  getField: <K extends keyof F9051>(key: K) => F9051[K] | string;
  setField: <K extends keyof F9051>(key: K, value: F9051[K]) => void;
  resolveSingleName: (nameKey: string, type: string, code: string) => void;
  onEmployeeBlur: (empCode: string) => void;
  getFieldError?: (field: string) => string | undefined;
}

const parseDateValue = (val: string | null | undefined) => {
  if (!val) return null;
  const d = dayjs(val);
  return d.isValid() ? d : null;
};

const EmployeeLookupWithName: React.FC<{
  nameKey: string;
  names: Record<string, string>;
  value: string;
  disabled: boolean;
  placeholder: string;
  onChange: (v: string) => void;
  onBlur?: () => void;
  width?: number;
}> = ({ nameKey, names, value, disabled, placeholder, onChange, onBlur, width = 120 }) => (
  <div className="pw9205-lookup-field">
    <LookupField
      lookupType="employee"
      value={value || ''}
      onChange={(code, name) => {
        onChange(code);
        if (onBlur) onBlur();
      }}
      disabled={disabled}
      style={{ width }}
      title={placeholder}
    />
    <span className="pw9205-lookup-name">{names[nameKey] || '--'}</span>
  </div>
);

const FormFields: React.FC<FormFieldsProps> = ({
  names, isEditing, mode,
  getField, setField, resolveSingleName, onEmployeeBlur, getFieldError,
}) => {
  const E = (field: string) => <FieldError error={getFieldError?.(field)} />;
  const isAddMode = mode === 'add';

  return (
    <div className="pw9205-form-fields">

      {/* ── 基本資訊 ── */}
      <div className="pw9205-section-label">基本資訊</div>

      {/* Row 1: Doc No + Change Date */}
      <div className="pw9205-field-row">
        <div className="pw9205-field-group">
          <label>異動單號{E('f9051_01')}</label>
          <Input
            style={{ width: 180 }}
            disabled
            value={getField('f9051_01') as string}
            placeholder="系統自動產生"
          />
        </div>
        <div className="pw9205-field-group">
          <label>異動日期{E('f9051_10')}</label>
          <DatePicker
            style={{ width: 160 }}
            disabled={!isEditing}
            value={parseDateValue(getField('f9051_10') as string | null)}
            onChange={(_date, dateStr) => {
              const v = Array.isArray(dateStr) ? dateStr[0] : dateStr;
              setField('f9051_10', v || null);
            }}
            placeholder="選擇日期"
            format="YYYY-MM-DD"
          />
        </div>
      </div>

      {/* Row 2: Employee */}
      <div className="pw9205-field-row">
        <div className="pw9205-field-group">
          <label className="required">員工編號{E('f9051_02')}</label>
          <EmployeeLookupWithName
            nameKey="employee"
            names={names}
            value={getField('f9051_02') as string}
            disabled={!isAddMode}
            placeholder="搜尋員工"
            onChange={v => setField('f9051_02', v)}
            onBlur={() => onEmployeeBlur(getField('f9051_02') as string)}
          />
        </div>
      </div>

      {/* ── 原代理人 (readonly) ── */}
      <div className="pw9205-section-label">原代理人</div>

      {[1, 2, 3, 4, 5].map(i => {
        const fieldKey = `f9051_${10 + i}` as keyof F9051;
        const nameKey = `origProxy${i}`;
        return (
          <div className="pw9205-field-row" key={`orig-${i}`}>
            <div className="pw9205-field-group">
              <label>原代理人{i}</label>
              <EmployeeLookupWithName
                nameKey={nameKey}
                names={names}
                value={getField(fieldKey) as string}
                disabled
                placeholder={`原代理人${i}`}
                onChange={() => {/* readonly */}}
              />
            </div>
          </div>
        );
      })}

      {/* ── 新代理人 (editable) ── */}
      <div className="pw9205-section-label">新代理人</div>

      {[1, 2, 3, 4, 5].map(i => {
        const fieldKey = `f9051_${30 + i}` as keyof F9051;
        const nameKey = `newProxy${i}`;
        return (
          <div className="pw9205-field-row" key={`new-${i}`}>
            <div className="pw9205-field-group">
              <label>新代理人{i}{E(fieldKey)}</label>
              <EmployeeLookupWithName
                nameKey={nameKey}
                names={names}
                value={getField(fieldKey) as string}
                disabled={!isEditing}
                placeholder={`搜尋新代理人${i}`}
                onChange={v => setField(fieldKey, v)}
                onBlur={() => resolveSingleName(nameKey, 'employee', getField(fieldKey) as string)}
              />
            </div>
          </div>
        );
      })}

      {/* ── 異動說明 ── */}
      <div className="pw9205-section-label">說明與備註</div>

      <div className="pw9205-field-row">
        <div className="pw9205-field-group pw9205-field-full">
          <label>異動說明{E('f9051_30')}</label>
          <TextArea
            style={{ width: 520 }}
            rows={3}
            disabled={!isEditing}
            value={getField('f9051_30') as string}
            onChange={e => setField('f9051_30', e.target.value)}
            placeholder="異動說明"
          />
        </div>
      </div>

      {/* ── 備註欄 (5 rows) ── */}
      {[1, 2, 3, 4, 5].map(i => {
        const fieldKey = `f9051_${35 + i}` as keyof F9051;
        return (
          <div className="pw9205-field-row" key={`remark-${i}`}>
            <div className="pw9205-field-group pw9205-field-wide">
              <label>代理人說明{i}{E(fieldKey)}</label>
              <TextArea
                style={{ width: 360 }}
                rows={2}
                disabled={!isEditing}
                value={getField(fieldKey) as string}
                onChange={e => setField(fieldKey, e.target.value)}
                placeholder={`代理人說明${i}`}
              />
            </div>
          </div>
        );
      })}

      {/* ── 稽核資訊 ── */}
      {(getField('f9051_91') || getField('f9051_92') || getField('f9051_93')) && (
        <div className="pw9205-field-row pw9205-audit-row">
          {getField('f9051_91') && (
            <span className="pw9205-audit-label">建立者：{getField('f9051_91') as string}</span>
          )}
          {getField('f9051_92') && (
            <span className="pw9205-audit-label">建立日期：{getField('f9051_92') as string}</span>
          )}
          {getField('f9051_93') && (
            <span className="pw9205-audit-label">修改者：{getField('f9051_93') as string}</span>
          )}
        </div>
      )}
    </div>
  );
};

export default React.memo(FormFields);
